import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  RefreshCw, CheckCircle2, XCircle, Link2, Package,
  Loader2, ArrowRight, Shield, AlertTriangle, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import ItemSearchCombobox from '../components/ItemSearchCombobox';
import { AppDrawer } from '../components/ui/AppDrawer';

/* ── Status config ─────────────────────────────────────────── */
const STATUS_CFG = {
  unmatched:     { label: 'Needs Match',        cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  pending:       { label: 'Ready to Deduct',    cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  approved:      { label: 'Deducted',           cls: 'bg-green-100 text-green-800 border-green-200' },
  auto_deducted: { label: 'Auto-Deducted',      cls: 'bg-green-100 text-green-800 border-green-200' },
  skipped:       { label: 'Skipped',            cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const CONFIDENCE_CFG = {
  high:   { icon: Shield,          cls: 'text-green-600', label: 'Exact' },
  medium: { icon: ChevronRight,    cls: 'text-blue-500',  label: 'Name' },
  low:    { icon: AlertTriangle,   cls: 'text-amber-500', label: 'Fuzzy' },
};

const fmtCents = (c) => c != null ? `$${(c / 100).toFixed(2)}` : '—';

/* ── Component ─────────────────────────────────────────────── */
export default function HCPDeductions() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 30;

  // Selection for bulk actions
  const [selected, setSelected] = useState(new Set());

  // Match drawer
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchTarget, setMatchTarget] = useState(null);      // single or null for bulk
  const [matchBulkIds, setMatchBulkIds] = useState([]);       // IDs for bulk match
  const [matchItemId, setMatchItemId] = useState(null);
  const [matchItemName, setMatchItemName] = useState('');
  const [busyIds, setBusyIds] = useState({});

  /* ── Data fetching ──────────────────────────────────────── */
  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (filter) params.status = filter;
      const r = await api.get('/integrations/housecall-pro/material-deductions', { params });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
      setSummary(r.data.summary || {});
      setSelected(new Set());
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { fetch(); }, [fetch]);

  /* ── Sync (background task) ─────────────────────────────── */
  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/integrations/housecall-pro/sync-invoice-deductions');
      const taskId = data.task_id;
      if (!taskId) { toast.error('Failed to start sync'); setSyncing(false); return; }
      toast.info('Syncing material usage from HCP jobs & invoices…');

      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const s = await api.get(`/integrations/housecall-pro/sync-invoice-deductions/${taskId}`);
          if (s.data.status === 'completed') {
            const jli = s.data.job_line_items || {};
            const inv = s.data.invoices || {};
            toast.success(`Sync done — ${jli.created || 0} new from jobs, ${inv.reconciled || 0} reconciled`);
            fetch();
            setSyncing(false);
            return;
          }
          if (s.data.status === 'failed') {
            toast.error(`Sync failed: ${s.data.error || 'Unknown'}`);
            setSyncing(false);
            return;
          }
        } catch { /* keep polling */ }
      }
      toast.warning('Sync still running — check back shortly.');
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  /* ── Single actions ─────────────────────────────────────── */
  const doApprove = async (id) => {
    setBusyIds(p => ({ ...p, [id]: true }));
    try {
      await api.post(`/integrations/housecall-pro/material-deductions/${id}/approve`, {});
      toast.success('Approved — inventory deducted');
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusyIds(p => ({ ...p, [id]: false })); }
  };

  const doSkip = async (id) => {
    setBusyIds(p => ({ ...p, [id]: true }));
    try {
      await api.post(`/integrations/housecall-pro/material-deductions/${id}/skip`);
      fetch();
    } catch { toast.error('Failed'); }
    finally { setBusyIds(p => ({ ...p, [id]: false })); }
  };

  /* ── Bulk actions ───────────────────────────────────────── */
  const bulkApprove = async () => {
    const ids = [...selected].filter(id => {
      const r = rows.find(x => x.id === id);
      return r && r.status === 'pending' && r.sl_item_id;
    });
    if (!ids.length) { toast.info('No approvable items selected'); return; }
    let ok = 0;
    for (const id of ids) {
      try { await api.post(`/integrations/housecall-pro/material-deductions/${id}/approve`, {}); ok++; } catch { /* skip */ }
    }
    toast.success(`Approved ${ok} deduction${ok > 1 ? 's' : ''} — inventory updated`);
    fetch();
  };

  const bulkSkip = async () => {
    const ids = [...selected].filter(id => {
      const r = rows.find(x => x.id === id);
      return r && (r.status === 'unmatched' || r.status === 'pending');
    });
    if (!ids.length) { toast.info('No skippable items selected'); return; }
    let ok = 0;
    for (const id of ids) {
      try { await api.post(`/integrations/housecall-pro/material-deductions/${id}/skip`); ok++; } catch { /* skip */ }
    }
    toast.success(`Skipped ${ok} item${ok > 1 ? 's' : ''}`);
    fetch();
  };

  const openBulkMatch = () => {
    const ids = [...selected].filter(id => {
      const r = rows.find(x => x.id === id);
      return r && r.status === 'unmatched';
    });
    if (!ids.length) { toast.info('No unmatchable items selected'); return; }
    setMatchTarget(null);
    setMatchBulkIds(ids);
    setMatchItemId(null);
    setMatchItemName('');
    setMatchOpen(true);
  };

  const openSingleMatch = (d) => {
    setMatchTarget(d);
    setMatchBulkIds([]);
    setMatchItemId(null);
    setMatchItemName('');
    setMatchOpen(true);
  };

  const handleMatchConfirm = async () => {
    if (!matchItemId) return;
    const ids = matchTarget ? [matchTarget.id] : matchBulkIds;
    let ok = 0;
    for (const id of ids) {
      try {
        const resp = await api.post(`/integrations/housecall-pro/material-deductions/${id}/match`, { sl_item_id: matchItemId });
        if (resp.data.mapping_created) {
          toast.success(`Mapping saved — all future "${matchTarget?.hcp_item_name || 'items'}" will auto-match`, { duration: 4000 });
        }
        ok++;
      } catch { /* skip */ }
    }
    if (ok > 0) toast.success(`Matched ${ok} item${ok > 1 ? 's' : ''}`);
    setMatchOpen(false);
    fetch();
  };

  /* ── Selection helpers ──────────────────────────────────── */
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };
  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const hasActionable = [...selected].some(id => {
    const r = rows.find(x => x.id === id);
    return r && r.status !== 'approved' && r.status !== 'auto_deducted' && r.status !== 'skipped';
  });

  /* ── Derived ────────────────────────────────────────────── */
  const unmatched  = summary.unmatched || 0;
  const pending    = summary.pending || 0;
  const deducted   = (summary.approved || 0) + (summary.auto_deducted || 0);
  const skippedCt  = summary.skipped || 0;
  const totalPages = Math.ceil(total / pageSize);

  /* ── Confidence badge ───────────────────────────────────── */
  const ConfBadge = ({ method, confidence }) => {
    if (!method) return null;
    const cfg = CONFIDENCE_CFG[confidence] || CONFIDENCE_CFG.low;
    const Icon = cfg.icon;
    const label = method === 'pbmat_mapping' ? 'Mapped' : method === 'manual' ? 'Manual' : cfg.label;
    return (
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${cfg.cls}`} title={`Match: ${method} (${confidence})`}>
        <Icon className="w-3 h-3" />{label}
      </span>
    );
  };

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8" data-testid="hcp-deductions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Material Deductions</h1>
            <p className="text-sm text-slate-500 mt-0.5">Review HCP job materials and deduct from S&L inventory</p>
          </div>
          <Button onClick={handleSync} disabled={syncing} data-testid="sync-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync Materials'}
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="summary-cards">
          <Card className="border-amber-200 bg-amber-50/50"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{unmatched}</p>
            <p className="text-xs text-amber-600">Needs Match</p>
          </CardContent></Card>
          <Card className="border-blue-200 bg-blue-50/50"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{pending}</p>
            <p className="text-xs text-blue-600">Ready to Deduct</p>
          </CardContent></Card>
          <Card className="border-green-200 bg-green-50/50"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{deducted}</p>
            <p className="text-xs text-green-600">Deducted</p>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-slate-500">{skippedCt}</p>
            <p className="text-xs text-slate-400">Skipped</p>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {[
            { v: '', l: 'All' },
            { v: 'unmatched', l: `Needs Match${unmatched ? ` (${unmatched})` : ''}` },
            { v: 'pending',   l: `Ready${pending ? ` (${pending})` : ''}` },
            { v: 'approved',  l: 'Deducted' },
            { v: 'skipped',   l: 'Skipped' },
          ].map(f => (
            <Button key={f.v} variant={filter === f.v ? 'default' : 'outline'} size="sm"
              onClick={() => { setFilter(f.v); setPage(1); }} data-testid={`filter-${f.v || 'all'}`}>
              {f.l}
            </Button>
          ))}
        </div>

        {/* Bulk action bar — appears between filters and table when rows selected */}
        {selected.size > 0 && hasActionable && (
          <div className="flex items-center gap-2 px-3 py-2 mb-2 bg-slate-50 dark:bg-slate-800 border rounded-lg" data-testid="bulk-bar">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{selected.size} selected</span>
            <div className="h-4 w-px bg-slate-300 mx-1" />
            {[...selected].some(id => rows.find(x => x.id === id)?.status === 'unmatched') && (
              <Button size="sm" variant="outline" onClick={openBulkMatch} data-testid="bulk-match-btn">
                <Link2 className="w-3.5 h-3.5 mr-1" />Match
              </Button>
            )}
            {[...selected].some(id => rows.find(x => x.id === id)?.status === 'pending') && (
              <Button size="sm" onClick={bulkApprove} data-testid="bulk-approve-btn">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve Selected
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={bulkSkip} data-testid="bulk-skip-btn">
              <XCircle className="w-3.5 h-3.5 mr-1" />Skip
            </Button>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setSelected(new Set())} data-testid="clear-selection">
              Clear
            </Button>
          </div>
        )}

        {/* Table / Loading / Empty */}
        {loading && !rows.length ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
            <Package className="w-8 h-8 text-slate-300 mb-3" />
            <h3 className="text-base font-medium text-slate-600">
              {filter ? 'No deductions match this filter' : 'No material deductions yet'}
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              {!filter && 'Click "Sync Materials" to pull usage from HCP jobs.'}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block border rounded-lg overflow-hidden" data-testid="deductions-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 px-3">
                      <Checkbox checked={selected.size === rows.length && rows.length > 0}
                        onCheckedChange={toggleAll} data-testid="select-all" />
                    </TableHead>
                    <TableHead className="w-[32%]">HCP Material</TableHead>
                    <TableHead className="w-[8%] text-center">Qty</TableHead>
                    <TableHead className="w-[10%] text-right">Price</TableHead>
                    <TableHead className="w-[22%]">S&L Match</TableHead>
                    <TableHead className="w-[10%]">Status</TableHead>
                    <TableHead className="w-[18%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(d => {
                    const sCfg = STATUS_CFG[d.status] || STATUS_CFG.unmatched;
                    const isFinished = d.status === 'approved' || d.status === 'auto_deducted' || d.status === 'skipped';
                    return (
                      <TableRow key={d.id} className={isFinished ? 'opacity-60' : ''} data-testid={`row-${d.id}`}>
                        <TableCell className="px-3">
                          {!isFinished && <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggle(d.id)} />}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <span className="font-medium text-sm block truncate">{d.hcp_item_name}</span>
                            <div className="flex items-center gap-1 text-[11px] text-slate-400">
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 font-medium ${d.source_type === 'job_line_item' ? 'border-indigo-200 text-indigo-600 bg-indigo-50' : 'border-slate-200 text-slate-500 bg-slate-50'}`}>
                                {d.source_type === 'job_line_item' ? 'Job' : 'Invoice'}
                              </Badge>
                              {d.hcp_invoice_number && (
                                <span className="tabular-nums">Inv #{d.hcp_invoice_number}</span>
                              )}
                              {d.hcp_service_item_id && (
                                <span className="font-mono text-slate-300 border border-slate-200 rounded px-1" title={d.hcp_service_item_id}>
                                  pbmat:{d.hcp_service_item_id.replace('pbmat_', '').substring(0, 8)}
                                </span>
                              )}
                              {d.invoice_reconciled && (
                                <CheckCircle2 className="w-3 h-3 text-green-500" title="Reconciled with invoice" />
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-medium tabular-nums">{d.hcp_qty}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{fmtCents(d.hcp_unit_price)}</TableCell>
                        <TableCell>
                          {d.sl_item_name ? (
                            <div className="flex items-center gap-1.5">
                              <Link2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                              <span className="text-sm truncate">{d.sl_item_name}</span>
                              <ConfBadge method={d.match_method} confidence={d.match_confidence} />
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">No match</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[11px] ${sCfg.cls}`}>{sCfg.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            {d.status === 'unmatched' && (
                              <Button size="sm" variant="outline" onClick={() => openSingleMatch(d)} data-testid={`match-${d.id}`}>
                                <Link2 className="w-3.5 h-3.5 mr-1" />Match
                              </Button>
                            )}
                            {d.status === 'pending' && (
                              <Button size="sm" onClick={() => doApprove(d.id)} disabled={busyIds[d.id]} data-testid={`approve-${d.id}`}>
                                {busyIds[d.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                                Approve
                              </Button>
                            )}
                            {!isFinished && (
                              <Button size="sm" variant="ghost" onClick={() => doSkip(d.id)} disabled={busyIds[d.id]}
                                data-testid={`skip-${d.id}`} title="Skip this deduction">
                                <XCircle className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2" data-testid="deductions-mobile">
              {rows.map(d => {
                const sCfg = STATUS_CFG[d.status] || STATUS_CFG.unmatched;
                const isFinished = d.status === 'approved' || d.status === 'auto_deducted' || d.status === 'skipped';
                return (
                  <Card key={d.id} className={`overflow-hidden ${isFinished ? 'opacity-60' : ''}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{d.hcp_item_name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <Badge variant="outline" className={`text-[10px] px-1 py-0 ${d.source_type === 'job_line_item' ? 'border-indigo-300 text-indigo-600 bg-indigo-50' : 'border-slate-300 text-slate-500'}`}>
                              {d.source_type === 'job_line_item' ? 'Job' : 'Inv'}
                            </Badge>
                            <span className="text-[10px] text-slate-500">Qty: {d.hcp_qty}</span>
                            <span className="text-[10px] text-slate-500">{fmtCents(d.hcp_unit_price)}</span>
                            {d.hcp_invoice_number && <span className="text-[10px] text-slate-400">#{d.hcp_invoice_number}</span>}
                          </div>
                          {d.sl_item_name && (
                            <div className="flex items-center gap-1 mt-1.5 text-xs">
                              <ArrowRight className="w-3 h-3 text-green-600" />
                              <span className="text-green-700 truncate">{d.sl_item_name}</span>
                              <ConfBadge method={d.match_method} confidence={d.match_confidence} />
                            </div>
                          )}
                        </div>
                        <Badge variant="outline" className={`text-[10px] shrink-0 ${sCfg.cls}`}>{sCfg.label}</Badge>
                      </div>
                      {!isFinished && (
                        <div className="flex gap-2 mt-2 pt-2 border-t">
                          {d.status === 'unmatched' && (
                            <Button size="sm" variant="outline" className="flex-1" onClick={() => openSingleMatch(d)}>
                              <Link2 className="w-3.5 h-3.5 mr-1" />Match
                            </Button>
                          )}
                          {d.status === 'pending' && (
                            <Button size="sm" className="flex-1" onClick={() => doApprove(d.id)} disabled={busyIds[d.id]}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => doSkip(d.id)}><XCircle className="w-3.5 h-3.5" /></Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 text-sm">
                <span className="text-slate-500">{total} total</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <span className="px-2 py-1 text-slate-500">{page}/{totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Match Drawer ──────────────────────────────────── */}
      <AppDrawer
        open={matchOpen}
        onOpenChange={setMatchOpen}
        title={matchTarget ? 'Match to S&L Item' : `Bulk Match (${matchBulkIds.length} items)`}
        description={matchTarget
          ? `Map "${matchTarget.hcp_item_name}" to a Stock & Load inventory item`
          : 'Select an S&L item to map all selected HCP materials to'}
        footer={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setMatchOpen(false)}>Cancel</Button>
            <Button onClick={handleMatchConfirm} disabled={!matchItemId} data-testid="confirm-match-btn">
              <Link2 className="w-4 h-4 mr-1.5" />
              {matchTarget ? 'Confirm Match' : `Match ${matchBulkIds.length} Items`}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {matchTarget && (
            <div className="p-3 bg-slate-50 rounded-lg space-y-1">
              <p className="text-xs text-slate-500">HCP Material</p>
              <p className="font-medium">{matchTarget.hcp_item_name}</p>
              <div className="flex gap-3 text-xs text-slate-500 flex-wrap">
                <span>Qty: {matchTarget.hcp_qty}</span>
                <span>Price: {fmtCents(matchTarget.hcp_unit_price)}</span>
                {matchTarget.hcp_service_item_id && (
                  <span className="font-mono text-slate-400">{matchTarget.hcp_service_item_id}</span>
                )}
              </div>
              {matchTarget.hcp_service_item_id && (
                <p className="text-xs text-indigo-600 mt-1">
                  This match will be saved for all future occurrences of this material.
                </p>
              )}
            </div>
          )}
          <div>
            <p className="text-sm font-medium mb-2">Select S&L Inventory Item</p>
            <ItemSearchCombobox
              value={matchItemName}
              itemId={matchItemId}
              onChange={(v) => setMatchItemName(v)}
              onSelect={(sel) => { setMatchItemId(sel.id); setMatchItemName(sel.name); }}
              placeholder="Search inventory items…"
              data-testid="match-item-combobox"
            />
          </div>
        </div>
      </AppDrawer>
    </Layout>
  );
}
